import React, { useEffect, useMemo, useState } from "react";
import { Tree, Input, Tag, Typography, Spin, Empty } from "antd";
import { SearchOutlined, FolderOutlined, TagOutlined } from "@ant-design/icons";
import useIsMobile from "../../hooks/useIsMobile.js";
import { renderInlineMarkdown } from "./markdownInline.js";

const { Text, Paragraph, Title } = Typography;

// Disciplines/<discipline>/Level of Information — renders the discipline's own Excel-exported
// item type metadata sheet (server/bimframeworkLoi.js parses the raw XML) as a master-detail
// view: a tree grouped by Pick_List family on the left (the same grouping ORD itself uses when
// an author picks an item type), a clean metadata card for whichever item type is selected on
// the right. Placeholder "-"/"Not_Set" values are already nulled out server-side, so the card
// only ever shows fields that actually apply to that item type instead of a wall of dashes.

const FOLDER_KEY = (family) => `f:${family}`;
const ITEM_KEY = (family, name) => `${family}/i:${name}`;

// Every column except the ones already shown as the card's own title/tags/lead paragraph —
// order here is the display order, left to right then top to bottom in the metadata grid.
const METADATA_FIELDS = [
  "Item_Type_Family", "Dimensions", "Unit", "Unit_Weight", "Gauge", "Sleeper_Spacing",
  "Fastening_Type", "Rail_Seat_Cant", "Standart", "Related_Price_Group", "Related_Unit_Price",
  "Item_Type_Unique_ID",
];

function humanizeField(field) {
  return field.replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

function highlight(text, term) {
  if (!term) return text;
  const idx = text.toLowerCase().indexOf(term);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "rgba(79,156,249,0.45)", color: "inherit", borderRadius: 2 }}>{text.slice(idx, idx + term.length)}</mark>
      {text.slice(idx + term.length)}
    </>
  );
}

function filterFamilies(families, term) {
  if (!term) return families;
  return families
    .map((f) => ({
      ...f,
      items: f.items.filter((item) =>
        item.Name.toLowerCase().includes(term) ||
        f.name.toLowerCase().includes(term) ||
        (item.Description || "").toLowerCase().includes(term)
      ),
    }))
    .filter((f) => f.items.length > 0);
}

function toTreeData(families, term) {
  return families.map((f) => ({
    key: FOLDER_KEY(f.name),
    title: (
      <span className="loi-row loi-row-folder">
        <FolderOutlined className="loi-icon" />
        <span className="loi-name">{highlight(f.name, term)}</span>
        <span className="loi-count">{f.items.length}</span>
      </span>
    ),
    children: f.items.map((item) => ({
      key: ITEM_KEY(f.name, item.Name),
      isLeaf: true,
      title: (
        <span className="loi-row loi-row-item">
          <TagOutlined className="loi-icon" />
          <span className="loi-name">{highlight(item.Name, term)}</span>
          {item.Type && (
            <Tag className="loi-tag-type" color={item.Type === "Physical" ? "blue" : "default"}>{item.Type}</Tag>
          )}
        </span>
      ),
    })),
  }));
}

function MetadataCard({ item }) {
  if (!item) {
    return <Empty description="Select an item type to view its metadata" style={{ marginTop: 40 }} />;
  }
  const fields = METADATA_FIELDS.filter((f) => item[f]);
  return (
    <div className="loi-card">
      <Title level={5} className="loi-card-title">{item.Name}</Title>
      <div className="loi-card-tags">
        {item.Type && <Tag color={item.Type === "Physical" ? "blue" : "default"}>{item.Type}</Tag>}
        {item["Type of Object"] && <Tag>{item["Type of Object"]}</Tag>}
      </div>
      {item.Description && (
        <Paragraph className="loi-card-description">{item.Description}</Paragraph>
      )}
      {fields.length > 0 ? (
        <div className="loi-card-grid">
          {fields.map((f) => (
            <div key={f} className="loi-card-field">
              <Text className="loi-card-field-label">{humanizeField(f)}</Text>
              <Text className="loi-card-field-value">{item[f]}</Text>
            </div>
          ))}
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 12.5 }}>No additional metadata for this item type.</Text>
      )}
    </div>
  );
}

export default function LevelOfInformationTree({ selectedKey, encodeSection, description }) {
  const isMobile = useIsMobile();
  const [families, setFamilies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const [manualExpanded, setManualExpanded] = useState([]);

  useEffect(() => {
    fetch(`/api/bimframework/level-of-information/${encodeSection(selectedKey)}`)
      .then((r) => r.json())
      .then((d) => setFamilies(d.families || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [selectedKey, encodeSection]);

  const term = searchInput.trim().toLowerCase();
  const filtered = useMemo(() => filterFamilies(families || [], term), [families, term]);
  const treeData = useMemo(() => toTreeData(filtered, term), [filtered, term]);
  // Always a real array — a Tree's expandedKeys switching between an array and undefined at
  // runtime flips it between controlled/uncontrolled, which antd doesn't handle cleanly (same
  // controlled-vs-search-expand pattern already proven in SymbologyTree.js's DisciplineTreeView).
  const searching = term.length > 0;
  const expandedKeys = searching ? treeData.map((f) => f.key) : manualExpanded;

  // Keyed from the full unfiltered list, not `filtered` — so a previously selected item's
  // detail card stays visible even if a later search term happens to filter it out of the
  // tree; only the tree's own visible rows should react to the search box.
  const itemsByKey = useMemo(() => {
    const map = new Map();
    for (const f of families || []) {
      for (const item of f.items) map.set(ITEM_KEY(f.name, item.Name), item);
    }
    return map;
  }, [families]);
  const selectedItem = selectedItemKey ? itemsByKey.get(selectedItemKey) : null;

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin size="large" /></div>;
  }
  if (error || !families || families.length === 0) {
    return <Empty description="No Level of Information sheet found for this discipline yet" style={{ marginTop: 40 }} />;
  }

  return (
    <div>
      <style>{`
        .loi-row { display: inline-flex; align-items: center; gap: 8px; }
        .loi-icon { color: rgba(255,255,255,0.35); font-size: 13px; }
        .loi-row-item .loi-icon { color: #4f9cf9; }
        .loi-name { font-family: 'DM Mono', monospace; font-size: 12.5px; }
        .loi-row-folder .loi-name { font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600; font-size: 13px; }
        .loi-count {
          font-family: 'DM Mono', monospace; font-size: 10.5px; color: rgba(255,255,255,0.35);
          background: rgba(255,255,255,0.06); border-radius: 999px; padding: 1px 8px;
        }
        .loi-tag-type { margin: 0; font-size: 10px; line-height: 16px; padding: 0 6px; }
        .loi-layout { display: flex; gap: 24px; align-items: flex-start; }
        .loi-tree-pane { width: 360px; flex-shrink: 0; }
        /* Some item type names + their Type tag run wider than the pane (longer names like
           "Rail_Track_Type01_Turnout_Track") — contained here with its own horizontal scroll
           instead of overflowing into the detail pane, same fix already used for the identical
           problem in SymbologyTree.js's feature rows. */
        .loi-tree { overflow-x: auto; }
        .loi-detail-pane { flex: 1; min-width: 0; }
        .loi-card {
          border: 1px solid rgba(255,255,255,0.1); border-radius: 12px;
          background: rgba(255,255,255,0.02); padding: 20px 24px;
        }
        .loi-card-title { margin: 0 0 8px !important; font-family: 'DM Mono', monospace !important; }
        .loi-card-tags { display: flex; gap: 6px; margin-bottom: 12px; }
        .loi-card-description { color: rgba(255,255,255,0.75); font-size: 13.5px; margin-bottom: 18px !important; }
        .loi-card-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px 20px;
        }
        .loi-card-field { display: flex; flex-direction: column; gap: 2px; }
        .loi-card-field-label {
          font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em;
          color: rgba(255,255,255,0.4); font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;
        }
        .loi-card-field-value { font-family: 'DM Mono', monospace; font-size: 13px; color: #fff; }
      `}</style>

      <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 18, maxWidth: 760 }}>
        {description
          ? renderInlineMarkdown(description)
          : "Item types grouped by Pick_List family, exactly as picked in ORD. Select one to see its full metadata."}
      </Paragraph>

      <Input
        placeholder="Search item types…"
        prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.3)" }} />}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        allowClear
        style={{ width: 260, marginBottom: 16 }}
      />

      <div className="loi-layout" style={isMobile ? { flexDirection: "column" } : undefined}>
        <div className="loi-tree-pane" style={isMobile ? { width: "100%" } : undefined}>
          {treeData.length === 0 ? (
            <Empty description="No matching item types" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Tree
              className="loi-tree"
              showLine={{ showLeafIcon: false }}
              blockNode
              treeData={treeData}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setManualExpanded(keys)}
              selectedKeys={selectedItemKey ? [selectedItemKey] : []}
              onSelect={(keys) => { if (keys.length) setSelectedItemKey(keys[0]); }}
            />
          )}
        </div>
        <div className="loi-detail-pane">
          <MetadataCard item={selectedItem} />
        </div>
      </div>
    </div>
  );
}
